import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Gates a route on authenticated session.
 *
 * Role gates use the EFFECTIVE identity (`user.role`) — i.e., the impersonated
 * role during View-As — so admins land on the same role-restricted pages a
 * normal user/dealer would. The single exception is `role="admin"`, which
 * always uses the REAL session role so admin can still reach /admin while
 * impersonating.
 */
export default function RequireAuth({ role, children }) {
  const { user, realUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div data-testid="auth-loading" className="min-h-[50vh] flex items-center justify-center">
        <div className="text-muted-foreground font-mono-data text-sm">Loading…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (role === "admin") {
    if (realUser?.role !== "admin") {
      const dest = user.role === "dealer" ? "/dealer" : "/dashboard";
      return <Navigate to={dest} replace />;
    }
    return children;
  }
  if (role && user.role !== role) {
    const dest = user.role === "admin" ? "/admin"
      : user.role === "dealer" ? "/dealer"
      : "/dashboard";
    return <Navigate to={dest} replace />;
  }
  return children;
}
